import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CheckIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
function CustomSelect({
  label,
  id: providedId,
  name,
  ariaLabel,
  value,
  options = [],
  onChange = () => {},
  onSearchChange,
  onSearchSubmit,
  returnOption,
  isClearable = false,
  placeholder = 'Pilih opsi',
  className = '',
  buttonClassName = '',
  compact = false,
  searchable = options.length >= 6,
  searchPlaceholder = 'Cari pilihan...',
  disabled = false,
  isDisabled = false,
  loading = false,
}) {
  const isOptionValue = value !== null && typeof value === 'object';
  const shouldReturnOption = returnOption ?? isOptionValue;
  const selectedValue = isOptionValue ? value.value : value;
  const isControlDisabled = disabled || isDisabled;
  const generatedId = useId();
  const id = providedId || generatedId;
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const searchRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(() =>
    Math.max(
      0,
      options.findIndex((item) => item.value === selectedValue)
    )
  );
  const selected =
    options.find((item) => item.value === selectedValue) || (isOptionValue ? value : null);
  const normalizedSearch = search.trim().toLocaleLowerCase('id-ID');
  const filteredOptions =
    searchable && normalizedSearch
      ? options.filter((option) =>
          [option.label, option.description, option.keywords]
            .filter(Boolean)
            .join(' ')
            .toLocaleLowerCase('id-ID')
            .includes(normalizedSearch)
        )
      : options;
  const listboxId = `${id}-listbox`;

  useLayoutEffect(() => {
    if (!open) return undefined;
    const positionMenu = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      const padding = 8;
      const gap = 6;
      const below = window.innerHeight - rect.bottom - padding - gap;
      const above = rect.top - padding - gap;
      const opensAbove = below < 280 && above > below;
      const width = Math.min(Math.max(rect.width, 210), window.innerWidth - padding * 2);
      setMenuPosition({
        position: 'fixed',
        width,
        left: Math.max(padding, Math.min(rect.left, window.innerWidth - width - padding)),
        maxHeight: Math.max(80, Math.min(360, opensAbove ? above : below)),
        ...(opensAbove
          ? { bottom: window.innerHeight - rect.top + gap }
          : { top: rect.bottom + gap }),
        zIndex: 9999,
      });
    };
    const closeOnScroll = (event) => {
      if (!menuRef.current?.contains(event.target)) setOpen(false);
    };
    positionMenu();
    window.addEventListener('resize', positionMenu);
    window.addEventListener('scroll', closeOnScroll, true);
    return () => {
      window.removeEventListener('resize', positionMenu);
      window.removeEventListener('scroll', closeOnScroll, true);
    };
  }, [open]);

  useEffect(() => {
    if (open && searchable) searchRef.current?.focus();
  }, [open, searchable]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutside = (event) => {
      if (
        rootRef.current &&
        !rootRef.current.contains(event.target) &&
        !menuRef.current?.contains(event.target)
      ) {
        setOpen(false);
        setSearch('');
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        setSearch('');
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', closeOnOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  const move = (next) => {
    if (!filteredOptions.length) return;
    setActiveIndex(Math.min(filteredOptions.length - 1, Math.max(0, next)));
  };
  const choose = (option) => {
    if (!option) return;
    onChange(shouldReturnOption ? option : option.value);
    setOpen(false);
    setSearch('');
    buttonRef.current?.focus();
  };
  const openMenu = () => {
    if (isControlDisabled) return;
    const selectedIndex = options.findIndex((item) => item.value === selectedValue);
    setSearch('');
    setActiveIndex(Math.max(0, selectedIndex));
    setOpen(true);
  };
  const handleKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) {
        openMenu();
      } else move(activeIndex + 1);
    }
    if (event.key === 'ArrowUp' && open) {
      event.preventDefault();
      move(activeIndex - 1);
    }
    if (event.key === 'Home' && open) {
      event.preventDefault();
      move(0);
    }
    if (event.key === 'End' && open) {
      event.preventDefault();
      move(filteredOptions.length - 1);
    }
    if ((event.key === 'Enter' || event.key === ' ') && open) {
      event.preventDefault();
      choose(filteredOptions[activeIndex]);
    }
    if (event.key === 'Escape' && open) {
      setOpen(false);
      setSearch('');
    }
  };
  const handleSearchKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      move(activeIndex + 1);
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      move(activeIndex - 1);
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (onSearchSubmit) onSearchSubmit(search);
      else choose(filteredOptions[activeIndex]);
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      setSearch('');
      buttonRef.current?.focus();
    }
  };

  return (
    <div ref={rootRef} className={`relative min-w-0 ${className}`}>
      {label && <div className='mb-1.5 text-xs font-medium text-[#445268]'>{label}</div>}
      <div className='relative'>
        <button
          ref={buttonRef}
          id={id}
          name={name}
          type='button'
          role='combobox'
          aria-label={ariaLabel || label || placeholder}
          aria-haspopup='listbox'
          aria-expanded={open}
          aria-controls={listboxId}
          aria-activedescendant={
            open && filteredOptions[activeIndex] ? `${id}-option-${activeIndex}` : undefined
          }
          disabled={isControlDisabled}
          onClick={() => {
            if (isControlDisabled) return;
            if (open) {
              setOpen(false);
              setSearch('');
            } else openMenu();
          }}
          onKeyDown={handleKeyDown}
          className={`flex min-h-10 w-full items-center justify-between gap-3 rounded-lg border bg-white px-3 text-left text-[13px] text-[#29384b] transition hover:border-[#b7c8cc] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-teal-700/10 disabled:cursor-not-allowed disabled:bg-[#f7f9f9] disabled:text-[#98a4aa] disabled:hover:border-[#d5dfe3] ${open ? 'border-[#90b9b2]' : 'border-[#d5dfe3]'} ${isClearable && selected ? 'pr-14' : ''} ${compact ? 'min-h-8 px-2.5 text-xs' : ''} ${buttonClassName}`}
        >
          <span className={`truncate ${selected ? '' : 'text-[#8a96a5]'}`}>
            {selected?.label || placeholder}
          </span>
          <span className='flex shrink-0 items-center gap-2'>
            {loading && (
              <span
                className='h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#d9e3e4] border-t-[#0f766e]'
                aria-label='Memuat pilihan'
              />
            )}
            <ChevronDownIcon
              className={`h-3.5 w-3.5 text-[#7f8d9b] transition ${open ? 'rotate-180' : ''}`}
              aria-hidden='true'
            />
          </span>
        </button>
        {isClearable && selected && !isControlDisabled && (
          <button
            type='button'
            aria-label={`Hapus pilihan ${ariaLabel || label || 'ini'}`}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              onChange(shouldReturnOption ? null : '');
              setOpen(false);
              setSearch('');
              buttonRef.current?.focus();
            }}
            className='absolute right-8 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-md text-[#8794a0] transition hover:bg-[#eff3f4] hover:text-[#455467] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-700/20'
          >
            <XMarkIcon className='h-3.5 w-3.5' aria-hidden='true' />
          </button>
        )}
      </div>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            style={menuPosition || { position: 'fixed', visibility: 'hidden' }}
            className='flex flex-col overflow-hidden rounded-xl border border-[#e1e8eb] bg-white shadow-[0_16px_36px_rgba(24,45,56,0.14)]'
          >
            {searchable && (
              <div className='shrink-0 border-b border-[#edf1f3] p-2.5'>
                <div className='relative'>
                  <MagnifyingGlassIcon
                    className='pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#8a97a4]'
                    aria-hidden='true'
                  />
                  <input
                    ref={searchRef}
                    type='text'
                    role='searchbox'
                    aria-label={`Cari ${ariaLabel || label || placeholder}`}
                    aria-controls={listboxId}
                    aria-activedescendant={
                      filteredOptions[activeIndex] ? `${id}-option-${activeIndex}` : undefined
                    }
                    autoComplete='off'
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setActiveIndex(0);
                      onSearchChange?.(event.target.value);
                    }}
                    onKeyDown={handleSearchKeyDown}
                    placeholder={searchPlaceholder}
                    className='h-9 w-full rounded-lg border border-[#e2e8eb] bg-[#fbfcfc] pl-8 pr-8 text-xs text-[#344256] outline-none transition placeholder:text-[#9aa5af] focus:border-[#8eb9b1] focus:bg-white focus:ring-2 focus:ring-[#0f766e]/10'
                  />
                  {search && (
                    <button
                      type='button'
                      aria-label='Hapus pencarian'
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        setSearch('');
                        setActiveIndex(0);
                        searchRef.current?.focus();
                      }}
                      className='absolute right-1.5 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-md text-[#8995a1] transition hover:bg-[#eef3f3] hover:text-[#435568]'
                    >
                      <XMarkIcon className='h-3.5 w-3.5' aria-hidden='true' />
                    </button>
                  )}
                </div>
                <p className='mt-1.5 px-0.5 text-[10px] text-[#8995a1]' aria-live='polite'>
                  {normalizedSearch
                    ? `${filteredOptions.length} dari ${options.length} pilihan`
                    : `${options.length} pilihan tersedia`}
                </p>
              </div>
            )}
            <div
              id={listboxId}
              role='listbox'
              aria-label={ariaLabel || label || placeholder}
              className='min-h-0 max-h-56 overflow-y-auto overscroll-contain p-1.5'
            >
              {filteredOptions.length ? (
                filteredOptions.map((option, index) => (
                  <div
                    id={`${id}-option-${index}`}
                    key={option.value}
                    role='option'
                    aria-selected={option.value === selectedValue}
                    tabIndex={-1}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => choose(option)}
                    className={`flex min-h-9 cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-xs transition ${index === activeIndex ? 'bg-[#f1f8f6] text-[#176b63]' : 'text-[#455467] hover:bg-[#f8fafb]'} ${option.value === selectedValue ? 'font-semibold' : 'font-normal'}`}
                  >
                    {option.dot && (
                      <span
                        className='h-1.5 w-1.5 shrink-0 rounded-full'
                        style={{ backgroundColor: option.dot }}
                        aria-hidden='true'
                      />
                    )}
                    <span className='min-w-0 flex-1'>
                      <span className='block truncate'>{option.label}</span>
                      {option.description && (
                        <span className='mt-0.5 block truncate text-[10px] font-normal text-[#8995a1]'>
                          {option.description}
                        </span>
                      )}
                    </span>
                    {option.value === selectedValue && (
                      <CheckIcon
                        className='h-3.5 w-3.5 shrink-0 text-[#168477]'
                        aria-hidden='true'
                      />
                    )}
                  </div>
                ))
              ) : (
                <div className='px-3 py-5 text-center' role='status'>
                  <p className='text-xs font-medium text-[#46566a]'>Tidak ada pilihan yang cocok</p>
                  <p className='mt-1 text-[10px] text-[#8a97a4]'>Coba kata kunci lain.</p>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

export default CustomSelect;
